// src/lib/use-daily-pricing.js — Phase 6 PR2 (ADR-093, Arbitrage B)
//
// Hook React qui charge en BATCH les prix journaliers de plusieurs logements
// pour CalendarPanel (TableauView multi-logements ; ex-MensuelView retirée
// PR #169d). Convention codebase : src/lib/use-*.js (cf. use-photo-upload-queue.js — seul précédent formel).
//
// Comportement obligatoire :
//   - Shortcircuit logementIds=[] (BPRv3-3) : aucun fetch déclenché
//   - AbortController (MPRv3-4/7) : annule fetch précédent si signature change
//   - cache MODULE (swr-cache) keyed sur signature stable [...ids].sort().join(',')|from|to
//     (MPRv3-7 ; Lot AUTO-REFRESH : survit au démontage d'onglet, stale-while-revalidate)
//   - pricingMap référence stable même contenu via cacheRef (BPRv3-4)
//   - modesByLogement.get(id) = 'pricelabs' | 'manual' | null (BPRv3-2 garantie endpoint)
//   - invalidate() exposé (BPR3-3 Phase 6 PR3) : vide cacheRef + force re-fetch
//
// Cf. ADR-093 (pivot UX intégration CalendarPanel) + plan v3.1 § 4.1.
// Cf. ADR-094 (Phase 6 PR3) invalidate() pattern.

import { useEffect, useState, useMemo, useCallback } from 'react'
import { supabase } from '../supabase'
import { swrGet, swrSet, swrIsFresh, swrClear } from './swr-cache'

// ── Lot AUTO-REFRESH (chantier « navigation sans re-fetch », audit §3/QW6) ──
// Le cache par signature est promu au NIVEAU MODULE (swr-cache) : il survit au
// démontage de CalendarPanel (navigation par onglets App.jsx) → retour au
// calendrier = rendu IMMÉDIAT depuis le cache. Stale-while-revalidate : une
// entrée plus vieille que DAILY_PRICING_FRESH_MS est servie quand même, puis
// refetchée SILENCIEUSEMENT en fond (loading reste false — pas de « prix
// vides »). Le premier rendu d'une session (cache vide) est inchangé.
export const DAILY_PRICING_FRESH_MS = 60_000
const DP_CACHE_PREFIX = 'dp|'

// ── Stable references ─────────────────────────────────────────────────
// Une seule Map vide réutilisée tant que logementIds=[] (référence stable).
const EMPTY_PRICING_MAP = new Map()
const EMPTY_MODES_MAP = new Map()
const EMPTY_BLOCKS_MAP = new Map()  // Phase 6.B PR5A — Map logement_id → array blocks
const EMPTY_MIN_STAY_DEFAULTS_MAP = new Map()  // PR #167c (ADR-101 / BPRc-3) — Map logement_id → int|null
// Fantômes calendrier — Map logement_id → array blocs annulés (fenêtre 30 j,
// champs minimaux sans argent, cf. payload cancelled_blocks batch_read).
const EMPTY_CANCELLED_BLOCKS_MAP = new Map()

/**
 * Charge prix journaliers en batch pour plusieurs logements.
 *
 * @param {string[]} logementIds — ids logements à charger (ex ['p1','p2','p6.lc1'])
 *   - TableauView : all visible logements (~11 max dont Maison Zen sub-locs)
 *   - TodayView (PR #169d) : consomme blocksByLogement aplati via flattenBlocksMap
 * @param {{ from: string, to: string }} dateRange — ISO YYYY-MM-DD
 * @returns {{
 *   pricingMap: Map<string, Map<string, { price1:number, num_avail:number, min_stay:number, fetched_at:string, is_manual_override:boolean }>>,
 *   modesByLogement: Map<string, 'pricelabs'|'manual'|null>,
 *     // null : logement présent en réponse mais sans config (BPRv3-2 garantie endpoint).
 *     // PAS undefined : l'endpoint batch retourne TOUS les ids reçus.
 *   blocksByLogement: Map<string, Array<{id, start_date, end_date, reason, beds24_pushed}>>,
 *   cancelledBlocksByLogement: Map<string, Array<{id, start_date, end_date, guest_first_name, guest_last_name, cancelled_at}>>,
 *     // Fantômes calendrier — blocs annulés (demande directe 'cancelled',
 *     // fenêtre 30 j glissants côté serveur). AUCUN champ argent.
 *   minStayDefaultsByLogement: Map<string, int|null>,
 *     // PR #167c (ADR-101 / BPRc-3) — défaut Niveau 1 priceRule[1].minimumStay
 *     // par logement. Sémantique : null = non géré FBZ, 1 = pas de restriction
 *     // explicite, >1 = restriction. Consommé par TableauView via
 *     // helper formatMinStayCellLabel pour badge override Niveau 3 vs défaut.
 *   loading: boolean,
 *   error: string|null,
 *   invalidate: () => void,
 * }}
 *
 * Pour décider d'afficher un prix :
 *   modesByLogement.get(id) != null && pricingMap.get(id)?.get(date)?.price1 != null
 */
export function useDailyPricing(logementIds, { from, to } = {}) {
  // Signature stable : tri pour invariance ordre array (MPRv3-7)
  const signature = useMemo(() => {
    if (!logementIds || logementIds.length === 0) return ''
    return [...logementIds].sort().join(',') + '|' + (from || '') + '|' + (to || '')
  }, [logementIds, from, to])

  // Lot AUTO-REFRESH : ex-cache useRef par instance → cache MODULE partagé
  // (swr-cache, clé DP_CACHE_PREFIX+signature) pour survivre au démontage de
  // l'onglet (navigation App.jsx par démontage/remontage).

  // BPR3-3 (Phase 6 PR3) : compteur interne pour forcer re-fetch via invalidate()
  const [refetchTick, setRefetchTick] = useState(0)

  const [state, setState] = useState({
    pricingMap: EMPTY_PRICING_MAP,
    modesByLogement: EMPTY_MODES_MAP,
    blocksByLogement: EMPTY_BLOCKS_MAP,  // Phase 6.B PR5A
    cancelledBlocksByLogement: EMPTY_CANCELLED_BLOCKS_MAP,  // Fantômes calendrier
    minStayDefaultsByLogement: EMPTY_MIN_STAY_DEFAULTS_MAP,  // PR #167c
    loading: false,
    error: null,
  })

  useEffect(() => {
    // BPRv3-3 : shortcircuit early si pas d'ids → zéro fetch
    if (!logementIds || logementIds.length === 0 || !from || !to) {
      setState({
        pricingMap: EMPTY_PRICING_MAP,
        modesByLogement: EMPTY_MODES_MAP,
        blocksByLogement: EMPTY_BLOCKS_MAP,
        cancelledBlocksByLogement: EMPTY_CANCELLED_BLOCKS_MAP,  // Fantômes calendrier
        minStayDefaultsByLogement: EMPTY_MIN_STAY_DEFAULTS_MAP,  // PR #167c
        loading: false,
        error: null,
      })
      return
    }

    // Cache hit (module, SWR) : restore référence stable (BPRv3-4), rendu
    // IMMÉDIAT. Entrée fraîche (≤ DAILY_PRICING_FRESH_MS) → pas de fetch
    // (comportement historique du cache). Entrée stale → servie quand même
    // (jamais de « prix vides » au retour d'onglet) PUIS refetch silencieux
    // en fond qui met à jour état + cache.
    const entry = swrGet(DP_CACHE_PREFIX + signature)
    const revalidating = !!entry
    if (entry) {
      const cached = entry.value
      setState({
        pricingMap: cached.pricingMap,
        modesByLogement: cached.modesByLogement,
        blocksByLogement: cached.blocksByLogement || EMPTY_BLOCKS_MAP,
        cancelledBlocksByLogement:
          cached.cancelledBlocksByLogement || EMPTY_CANCELLED_BLOCKS_MAP,  // Fantômes
        minStayDefaultsByLogement:
          cached.minStayDefaultsByLogement || EMPTY_MIN_STAY_DEFAULTS_MAP,  // PR #167c
        loading: false,
        error: null,
      })
      if (swrIsFresh(entry, DAILY_PRICING_FRESH_MS)) return
    }

    const controller = new AbortController()
    // Revalidation en fond : loading reste false (le cache est affiché) ;
    // premier fetch d'une signature : loading=true, inchangé.
    if (!revalidating) setState((prev) => ({ ...prev, loading: true, error: null }))

    ;(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (!session?.access_token) {
          if (controller.signal.aborted) return
          // Revalidation silencieuse : on garde la dernière vue valide (pattern
          // InboxPanel.refresh(silent)) — pas de bannière d'erreur.
          if (revalidating) { console.error('[useDailyPricing] silent revalidate: not_authenticated'); return }
          setState((prev) => ({ ...prev, loading: false, error: 'not_authenticated' }))
          return
        }

        const qs = new URLSearchParams({
          action: 'pricing_calendar_batch_read',
          logement_ids: logementIds.join(','),
          from,
          to,
        })
        const res = await fetch(`/api/beds24-mapping?${qs.toString()}`, {
          signal: controller.signal,
          headers: { Authorization: `Bearer ${session.access_token}` },
        })

        if (controller.signal.aborted) return  // MPRv3-7 stale closure protection

        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}))
          if (controller.signal.aborted) return  // NIT-1 PR3 : 2e check après await json
          // Revalidation silencieuse en échec → dernière vue conservée + log.
          if (revalidating) { console.error('[useDailyPricing] silent revalidate failed:', errBody.error || `http_${res.status}`); return }
          setState((prev) => ({
            ...prev,
            loading: false,
            error: errBody.error || `http_${res.status}`,
          }))
          return
        }

        const json = await res.json()
        if (controller.signal.aborted) return

        // Build outerMap + modesMap + blocksMap + minStayDefaultsMap depuis payload batch
        const newPricingMap = new Map()
        const newModesMap = new Map()
        const newBlocksMap = new Map()  // Phase 6.B PR5A (MPR5-4)
        const newCancelledBlocksMap = new Map()  // Fantômes calendrier
        const newMinStayDefaultsMap = new Map()  // PR #167c (ADR-101 / BPRc-3)
        for (const lg of (json.logements || [])) {
          newModesMap.set(lg.logement_id, lg.pricing_mode ?? null)  // BPRv3-2
          const innerMap = new Map()
          for (const item of (lg.items || [])) {
            innerMap.set(item.date, {
              price1: item.price1,
              num_avail: item.num_avail,
              min_stay: item.min_stay,
              fetched_at: item.fetched_at,
              // LOT 4 — verrou par date (badge 🔒 + bouton déverrouiller).
              is_manual_override: item.is_manual_override === true,
            })
          }
          newPricingMap.set(lg.logement_id, innerMap)
          // Phase 6.B PR5A : array blocks par logement (peut être []).
          newBlocksMap.set(lg.logement_id, Array.isArray(lg.blocks) ? lg.blocks : [])
          // Fantômes calendrier : array blocs annulés par logement (peut être []).
          // Rétrocompat : champ absent (vieux payload) → [].
          newCancelledBlocksMap.set(
            lg.logement_id,
            Array.isArray(lg.cancelled_blocks) ? lg.cancelled_blocks : [],
          )
          // PR #167c : min_stay_default Niveau 1 (nullable). Normalisation 0 → null
          // côté backend déjà appliquée (smoke S-2.bis garde). Côté client on
          // accepte tel quel — helper formatMinStayCellLabel re-normalise par
          // sécurité défense-en-profondeur.
          newMinStayDefaultsMap.set(
            lg.logement_id,
            lg.min_stay_default == null ? null : lg.min_stay_default,
          )
        }

        // MPRv3-2 : alerte Sentry breadcrumb si > 30% logements sans config
        const unknown = (json.logements || []).filter((l) => l.pricing_mode == null).length
        const total = (json.logements || []).length
        if (total > 0 && unknown / total > 0.3) {
          // eslint-disable-next-line no-console
          console.warn('[pricing-batch] > 30% logements_unknown', { unknown, total, ids: logementIds })
        }

        // Stocke dans le cache module pour référence stable (BPRv3-4) +
        // survie à la navigation par onglets (Lot AUTO-REFRESH).
        const cached = {
          pricingMap: newPricingMap,
          modesByLogement: newModesMap,
          blocksByLogement: newBlocksMap,  // Phase 6.B PR5A
          cancelledBlocksByLogement: newCancelledBlocksMap,  // Fantômes calendrier
          minStayDefaultsByLogement: newMinStayDefaultsMap,  // PR #167c
        }
        swrSet(DP_CACHE_PREFIX + signature, cached)
        setState({
          pricingMap: newPricingMap,
          modesByLogement: newModesMap,
          blocksByLogement: newBlocksMap,
          cancelledBlocksByLogement: newCancelledBlocksMap,  // Fantômes calendrier
          minStayDefaultsByLogement: newMinStayDefaultsMap,  // PR #167c
          loading: false,
          error: null,
        })
      } catch (e) {
        if (e.name === 'AbortError') return    // MPRv3-4/7
        if (controller.signal.aborted) return
        // Revalidation silencieuse en échec → dernière vue conservée + log.
        if (revalidating) { console.error('[useDailyPricing] silent revalidate failed:', e.message || e); return }
        setState((prev) => ({ ...prev, loading: false, error: e.message || String(e) }))
      }
    })()

    return () => controller.abort()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, refetchTick])

  // BPR3-3 (Phase 6 PR3) : invalidate() exposé pour force re-fetch après push manual.
  // useCallback([], []) deps vides → identité référentielle stable (ne casse pas
  // React.memo Row PR2 ni les useEffect des consumers).
  const invalidate = useCallback(() => {
    swrClear(DP_CACHE_PREFIX)
    setRefetchTick((t) => t + 1)
  }, [])

  // BPRv3-4 : retour mémoïsé référence stable
  return useMemo(() => ({
    pricingMap: state.pricingMap,
    modesByLogement: state.modesByLogement,
    blocksByLogement: state.blocksByLogement,  // Phase 6.B PR5A
    cancelledBlocksByLogement: state.cancelledBlocksByLogement,  // Fantômes calendrier
    minStayDefaultsByLogement: state.minStayDefaultsByLogement,  // PR #167c
    loading: state.loading,
    error: state.error,
    invalidate,
  }), [state.pricingMap, state.modesByLogement, state.blocksByLogement, state.cancelledBlocksByLogement, state.minStayDefaultsByLogement, state.loading, state.error, invalidate])
}
